// BackgroundChatListener.js - Listens for background chat completions and shows notifications
import logger from '../../../../../shared/services/logging/logger';
import { useEffect, useRef, useCallback } from 'react';
import { useGeneration } from '../../../../../shared/navigation/GenerationContext';
import { useToastNotificationContext } from '../../../../../shared/notifications/ToastNotificationContext';
import {
  initializeWebSocket,
  registerMessageHandler,
  disconnectFromWebSocket
} from '../../../hooks/useChatAnalyserApis';
import { checkResponseCompletion } from '../../../../scriptGeneration/components/chat/utils/validationHelpers';

/**
 * Headless component that listens for background chat completion events via WebSocket
 * Shows toast notifications when background analyses finish; renders no visible UI
 *
 * @component
 * @returns {null} Renders nothing - operates purely via side effects
 */
const BackgroundChatListener = () => {
  const {
    backgroundChats,
    completedChats,
    updateBackgroundChatContent,
    markChatCompleted,
    clearCompletedChat
  } = useGeneration();

  const { showSuccess } = useToastNotificationContext();
  const messageContentRef = useRef({});
  const unregisterRef = useRef(null);
  const isInitializedRef = useRef(false);
  const notifiedChatsRef = useRef(new Set());

  // Debug: Log component state on every render
  logger.info('🎧 BackgroundChatListener render:', {
    backgroundChatsCount: backgroundChats.length,
    backgroundChats: backgroundChats.map(c => ({ id: c.conversationId, title: c.title })),
    completedChatsCount: completedChats.length,
    isInitialized: isInitializedRef.current
  });

// Handler for incoming WebSocket messages
  const handleBackgroundMessage = useCallback((data) => {
    logger.info('🎧 handleBackgroundMessage called', { type: data.type, conversationId: data.conversationId, chatId: data.chatId, trackedChats: backgroundChats.map(c => c.conversationId) });

    let conversationId = data.conversationId;

    // The WebSocket `end` event fires as { type: 'complete', chatId } with no conversationId.
    // Fall back to the single active background chat — QIS only backgrounds one at a time.
    if (!conversationId && (data.type === 'complete' || data.type === 'streaming_error' || data.type === 'error')) {
      if (backgroundChats.length === 1) {
        conversationId = backgroundChats[0].conversationId;
        logger.info('🎧 No conversationId in message — falling back to single background chat:', conversationId);
      }
    }

    if (!conversationId) return;

    // Only process messages for tracked background chats
    const isTracked = backgroundChats.some(chat => chat.conversationId === conversationId);
    if (!isTracked) return;

    logger.info('📡 Background listener received message for:', conversationId, data.type);

    if (data.type === 'thinking_started') {
      logger.info('🧠 Background chat: extended thinking started', { conversationId });
      return;
    }

    // We don't need to accumulate content - the backend stores it
    // Just track that we're still streaming
    if (data.type === 'stream' && data.content) {
      updateBackgroundChatContent(conversationId, data.content);
    }

    // Check for completion - when complete, show notification
    if (data.type === 'complete') {
      logger.info('✅ Background chat completed:', conversationId);

      const chat = backgroundChats.find(c => c.conversationId === conversationId);
      const title = chat?.title || 'Analysis';

      logger.info('📤 Marking chat as completed:', { conversationId, title });
      markChatCompleted(conversationId, title);
    }

    // Streaming errors also end the analysis — remove from background so sidebar doesn't stay stuck
    if (data.type === 'streaming_error' || data.type === 'error') {
      logger.warn('⚠️ Background chat stream error:', { conversationId, type: data.type });
      const chat = backgroundChats.find(c => c.conversationId === conversationId);
      markChatCompleted(conversationId, chat?.title || 'Analysis');
    }
  }, [backgroundChats, updateBackgroundChatContent, markChatCompleted]);

  // Initialize WebSocket and register handler when we have background chats
  useEffect(() => {
    if (backgroundChats.length === 0) {
      if (unregisterRef.current) {
        logger.info('📴 No background chats, unregistering listener');
        unregisterRef.current();
        unregisterRef.current = null;
      }
      // Always reset initialized flag when no background chats
      isInitializedRef.current = false;
      return;
    }

    // Hoisted so cleanup can always set it, even when the init branch was skipped.
    let mounted = true;

    if (!isInitializedRef.current) {
      logger.info('🔌 Initializing background chat listener for', backgroundChats.length, 'chats');
      isInitializedRef.current = true;

      // Fire-and-forget: initialize WebSocket connection.
      // Guards against registering a handler after cleanup has already run
      // (happens if the component unmounts while initializeWebSocket is awaiting).
      (async () => {
        try {
          await initializeWebSocket();
          if (!mounted) return;
          logger.info('✅ Background chat listener WebSocket ready, registering handler');
          unregisterRef.current = registerMessageHandler(handleBackgroundMessageRef.current);
          logger.info('✅ Background handler registered, unregisterRef set:', !!unregisterRef.current);
        } catch (err) {
          logger.error('❌ Background chat listener WebSocket failed:', {
            error: err.message,
            stack: err.stack
          });
          if (mounted) isInitializedRef.current = false;
        }
      })();
    }

    return () => {
      mounted = false;
      logger.info('🧹 BackgroundChatListener effect cleanup, unregistering handler');
      if (unregisterRef.current) {
        unregisterRef.current();
        unregisterRef.current = null;
      }
      // Balance the initializeWebSocket() call above so activeConnectionCount stays accurate
      if (isInitializedRef.current) {
        disconnectFromWebSocket();
        isInitializedRef.current = false;
      }
    };
  }, [backgroundChats.length]);

  // Re-register handler when backgroundChats changes to have latest list
  // Uses a ref to avoid re-running the init effect on every backgroundChats change
  const handleBackgroundMessageRef = useRef(handleBackgroundMessage);
  useEffect(() => {
    handleBackgroundMessageRef.current = handleBackgroundMessage;
  }, [handleBackgroundMessage]);

  useEffect(() => {
    if (unregisterRef.current && backgroundChats.length > 0) {
      unregisterRef.current();
      unregisterRef.current = registerMessageHandler(handleBackgroundMessageRef.current);
    }
  }, [backgroundChats]);

useEffect(() => {
  completedChats.forEach(chat => {
    if (!notifiedChatsRef.current.has(chat.conversationId)) {
      notifiedChatsRef.current.add(chat.conversationId);

      logger.info('🔔 Showing completion notification for:', chat.conversationId);

      // Use relative path so React Router does SPA navigation (not a full page reload)
      const actionUrl = `/data-analyzer-chat?conversationId=${chat.conversationId}&refresh=true`;

      showSuccess(
        'Analysis Complete',
        `Your analysis is ready. Click to view.`,
        actionUrl
      );

      // Clear from completed after showing notification
      setTimeout(() => {
        clearCompletedChat(chat.conversationId);
        notifiedChatsRef.current.delete(chat.conversationId);
      }, 1000);
    }
  });
}, [completedChats, showSuccess, clearCompletedChat]);

  // This component doesn't render anything
  return null;
};

export default BackgroundChatListener;
